import React, { useState } from 'react';
import { 
    Store, PieChart, Users, ChefHat, 
    Sparkles, ArrowRight, CheckCircle2, 
    MonitorSmartphone, Shield, Zap
} from 'lucide-react';

const SystemFeatures = () => {
    // State to track which feature is currently selected in the interactive menu
    const [activeFeature, setActiveFeature] = useState(0);

    // Feature Data - Centralized for clean rendering
    const features = [
        {
            id: 'multi-branch',
            icon: Store,
            title: "Multi-Branch Command",
            subtitle: "Total control over every location.",
            description: "Stop logging into different systems for different branches. Manage all menus, pricing, staff, and analytics from one centralized headquarters dashboard.",
            benefits: ["Centralized Menu Management", "Branch-specific Analytics", "Global Inventory Sync"],
            image: "https://images.unsplash.com/photo-1552566626-52f8b828add9?q=80&w=1200&auto=format&fit=crop" // Moody restaurant interior
        },
        {
            id: 'hr-geofence',
            icon: Users,
            title: "Geofenced HR & Payroll",
            subtitle: "Never pay for time-theft again.",
            description: "Staff must be physically inside their assigned restaurant branch to clock in. The system automatically calculates hourly wages, overtime, and prepares single-click payroll.",
            benefits: ["GPS Location Verification", "Automated Payroll Calculation", "1-Click Branch Transfers"],
            image: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?q=80&w=1200&auto=format&fit=crop" // Team meeting / staff
        },
        {
            id: 'finance-roi',
            icon: PieChart,
            title: "True Net Profit Engine",
            subtitle: "Know your actual margins, instantly.",
            description: "Gross sales are vanity. Net profit is sanity. We automatically deduct your raw ingredient COGS, staff payroll, and utility expenses from your POS sales to show you the real numbers.",
            benefits: ["Live P&L Statements", "Labor Cost % Tracking", "Operating Expense Ledger"],
            image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop" // Data charts on screen
        },
        {
            id: 'kds',
            icon: ChefHat,
            title: "Live Kitchen Display",
            subtitle: "Silent, lightning-fast kitchen flow.",
            description: "Ditch the paper tickets. Orders instantly appear on the kitchen screen with visual timers. Chefs can mark items as cooking or ready, bridging the gap between FOH and BOH.",
            benefits: ["Real-time Order Routing", "Color-coded Prep Timers", "Reduced Ticket Times"],
            image: "https://images.unsplash.com/photo-1556740714-a8395b3bf30f?q=80&w=1200&auto=format&fit=crop" // Modern POS/Tablet setup
        }
    ];

    return (
        <div className="min-h-screen bg-slate-950 font-sans selection:bg-blue-500/30 pb-24">
            
            {/*}
            {/* --- TOP HERO SECTION WITH IMAGE --- */}
            <div className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden border-b border-slate-800">
                {/* Background Image */}
                <div 
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-40"
                    style={{ backgroundImage: "url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop')" }}
                ></div>
                {/* Heavy Gradient Overlay to ensure text readability */}
                <div className="absolute inset-0 z-0 bg-gradient-to-t from-slate-950 via-slate-950/80 to-slate-950/20"></div>
                <div className="absolute inset-0 z-0 bg-gradient-to-r from-slate-950 via-transparent to-slate-950"></div>

                <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur-md border border-slate-700 text-blue-400 text-xs font-bold uppercase tracking-widest mb-8">
                        <Sparkles size={14} className="text-blue-500" /> Platform Features
                    </div>
                    
                    {/* Constrained Typography to prevent text from spreading too wide */}
                    <div className="max-w-4xl mx-auto">
                        <h1 className="text-4xl md:text-5xl lg:text-7xl font-black text-white tracking-tight leading-[1.1] mb-6">
                            Everything you need. <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-400">
                                Nothing you don't.
                            </span>
                        </h1>
                        <p className="text-lg md:text-xl text-slate-400 font-medium max-w-2xl mx-auto leading-relaxed">
                            Jilapi replaces your fragmented tools with a single, deeply integrated ecosystem built specifically for multi-location restaurant operations.
                        </p>
                    </div>
                </div>
            </div>

            {/*}
            {/* --- INTERACTIVE SHOWCASE SECTION --- */}
            <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20">
                
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
                    
                    {/* LEFT SIDE: The Interactive Menu (Spans 4 columns) */}
                    <div className="lg:col-span-4 space-y-3 sticky top-24">
                        <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-6 ml-4">Core Capabilities</h3>
                        
                        {features.map((feature, idx) => (
                            <button
                                key={feature.id}
                                onClick={() => setActiveFeature(idx)}
                                className={`w-full text-left p-5 rounded-2xl transition-all duration-300 flex items-start gap-4 border ${
                                    activeFeature === idx 
                                        ? 'bg-slate-900 border-blue-500/30 shadow-[0_0_30px_rgba(59,130,246,0.1)]' 
                                        : 'bg-transparent border-transparent hover:bg-slate-900/50 hover:border-slate-800'
                                }`}
                            >
                                <div className={`p-3 rounded-xl transition-colors ${
                                    activeFeature === idx ? 'bg-blue-500/20 text-blue-400' : 'bg-slate-800 text-slate-400'
                                }`}>
                                    <feature.icon size={22} />
                                </div>
                                <div>
                                    <h4 className={`text-base font-bold mb-1 transition-colors ${
                                        activeFeature === idx ? 'text-white' : 'text-slate-300'
                                    }`}>
                                        {feature.title}
                                    </h4>
                                    <p className={`text-sm transition-colors ${
                                        activeFeature === idx ? 'text-blue-200/70' : 'text-slate-500'
                                    }`}>
                                        {feature.subtitle}
                                    </p>
                                </div>
                            </button>
                        ))}
                    </div>

                    {/*}
                    {/* RIGHT SIDE: The Dynamic Content Box (Spans 8 columns) */}
                    <div className="lg:col-span-8 relative">
                        {/* 
                            We map through features and toggle opacity/position to create a smooth crossfade effect 
                            without losing the structural height of the container.
                        */}
                        {features.map((feature, idx) => (
                            <div 
                                key={feature.id}
                                className={`transition-all duration-500 ${
                                    activeFeature === idx 
                                        ? 'opacity-100 translate-y-0 relative z-10' 
                                        : 'opacity-0 translate-y-4 absolute inset-0 pointer-events-none z-0'
                                }`}
                            >
                                {/* Premium Content Container */}
                                <div className="bg-slate-900/50 backdrop-blur-md border border-slate-800 rounded-[2rem] overflow-hidden shadow-2xl">
                                    
                                    {/* Image Area */}
                                    <div className="h-64 sm:h-80 w-full relative">
                                        <div 
                                            className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 hover:scale-105"
                                            style={{ backgroundImage: `url('${feature.image}')` }}
                                        ></div>
                                        {/* Gradient to blend image smoothly into the content below */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
                                        <div className="absolute inset-0 bg-blue-500/10 mix-blend-overlay"></div>
                                    </div>

                                    {/* Text & Details Area (Strictly contained for readability) */}
                                    <div className="p-8 sm:p-12 relative z-10 bg-slate-900">
                                        <div className="max-w-2xl">
                                            <h2 className="text-2xl sm:text-3xl font-black text-white mb-4">
                                                {feature.title}
                                            </h2>
                                            <p className="text-slate-400 leading-relaxed text-base sm:text-lg mb-8">
                                                {feature.description}
                                            </p>
                                            
                                            {/* Feature Benefits List */}
                                            <div className="space-y-4">
                                                {feature.benefits.map((benefit, bIdx) => (
                                                    <div key={bIdx} className="flex items-center gap-3 text-slate-300 font-medium">
                                                        <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center border border-blue-500/20 shrink-0">
                                                            <CheckCircle2 size={14} className="text-blue-400" />
                                                        </div>
                                                        {benefit}
                                                    </div>
                                                ))}
                                            </div>

                                            <button className="mt-10 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold flex items-center gap-2 transition-all shadow-lg shadow-blue-900/20 group">
                                                Explore this feature 
                                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                </div>
            </div>

            {/*}
            {/* --- BOTTOM QUICK STATS / TECH SPECS --- */}
            <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-32">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-12 border-t border-slate-800/80">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 text-slate-400">
                            <MonitorSmartphone size={24} />
                        </div>
                        <div>
                            <h4 className="text-white font-bold">Cloud Native</h4>
                            <p className="text-sm text-slate-500">Access anywhere, any device</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 text-slate-400">
                            <Shield size={24} />
                        </div>
                        <div>
                            <h4 className="text-white font-bold">Bank-Grade Security</h4>
                            <p className="text-sm text-slate-500">Role-based data locking</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-slate-900 rounded-xl border border-slate-800 text-slate-400">
                            <Zap size={24} />
                        </div>
                        <div>
                            <h4 className="text-white font-bold">Lightning Fast</h4>
                            <p className="text-sm text-slate-500">Zero lag during rush hour</p>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    );
};

export default SystemFeatures;